import { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import "./ChooseSection.css";

const features = [
    {
        id: 1,
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4" />
                <path d="M7 8h4M7 11h2" />
                <circle cx="16" cy="9" r="2" />
                <path d="M13 13c0-1.7 1.3-3 3-3s3 1.3 3 3" />
            </svg>
        ),
        title: "Data By Industry",
        subtitle: "Sector-Aware Targeting",
        text: "You can increase business connections with sectors that could become your future clients by using industry targeting.",
        stat: "50+",
        statLabel: "Industries Covered",
    },
    {
        id: 2,
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
        ),
        title: "Data By Country",
        subtitle: "Global Reach",
        text: "Filter your data selection based on location — search by radius, corridors, region, province, and more. Choose international data based on LATAM, NA, APAC, and EMEA.",
        stat: "6+",
        statLabel: "Countries Available",
    },
    {
        id: 3,
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                <path d="M12 12h.01M8 12h.01M16 12h.01" />
                <path d="M6 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
            </svg>
        ),
        title: "Data By Job Title",
        subtitle: "Role-Based Selection",
        text: "In the workplace, duties can vary greatly, thus it's critical to understand one's place within the company. Select your information based on the choices they are accountable for.",
        stat: "300+",
        statLabel: "Job Titles Tracked",
    },
    {
        id: 4,
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
        ),
        title: "Lead Generation",
        subtitle: "Quality Over Quantity",
        text: "LeadsQResearch helps companies generate more leads by creating high-quality content, running targeted advertising campaigns, and building relationships with key decision-makers.",
        stat: "500+",
        statLabel: "Leads Generated",
    },
    {
        id: 5,
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 17a1 1 0 0 0 2 0v-5a1 1 0 0 0-2 0v5z" />
                <path d="M12 7h.01" />
                <circle cx="12" cy="12" r="10" />
            </svg>
        ),
        title: "Research Service",
        subtitle: "Deep Intelligence",
        text: "Our analysts adeptly handle granular data, integrating and analyzing it to meet diverse client needs. They merge detailed internal data with external sources for comprehensive insights.",
        stat: "98%",
        statLabel: "Data Accuracy",
    },
    {
        id: 6,
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
        ),
        title: "Strategic Consulting",
        subtitle: "Actionable Strategy",
        text: "Market research has long involved tracking market numbers, conducting primary and secondary research, and analyzing market players. LeadsQResearch transforms strategy into actionable tasks for effective results.",
        stat: "15+",
        statLabel: "Clients Served",
    },
];

// Animate a number from 0 to target when visible
const useCountUp = (target, isVisible, duration = 1400) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!isVisible) return;
        const numeric = parseFloat(target.replace(/[^0-9.]/g, ""));
        const suffix = target.replace(/[0-9.]/g, "");
        let start = null;

        const step = (timestamp) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numeric) + suffix);
            if (progress < 1) requestAnimationFrame(step);
            else setCount(target);
        };
        requestAnimationFrame(step);
    }, [isVisible, target, duration]);

    return count;
};

const FeatureCard = ({ feature, index, isVisible }) => {
    const [hovered, setHovered] = useState(false);
    const animatedStat = useCountUp(feature.stat, isVisible);

    return (
        <Col lg={4} md={6} className={`cs-col ${isVisible ? "cs-col--visible" : ""}`} style={{ "--i": index }}>
            <Card
                className={`cs-card ${hovered ? "cs-card--hovered" : ""}`}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {/* Glow blob */}
                <div className="cs-card__glow" />

                <Card.Body className="cs-card__body">
                    {/* Icon */}
                    <div className="cs-icon-wrap">
                        <div className="cs-icon">{feature.icon}</div>
                    </div>

                    {/* Content */}
                    <div className="cs-card__content">
                        <p className="cs-card__subtitle">{feature.subtitle}</p>
                        <Card.Title className="cs-card__title">{feature.title}</Card.Title>
                        <Card.Text className="cs-card__text">{feature.text}</Card.Text>
                    </div>

                    {/* Stat pill — slides up on hover */}
                    <div className={`cs-stat ${hovered ? "cs-stat--visible" : ""}`}>
                        <span className="cs-stat__number">{animatedStat}</span>
                        <span className="cs-stat__label">{feature.statLabel}</span>
                    </div>
                </Card.Body>

                {/* Bottom accent line */}
                <div className="cs-card__line" />
            </Card>
        </Col>
    );
};

export default function () {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.15 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section className={`cs-section ${isVisible ? "cs-section--visible" : ""}`} ref={sectionRef}>
            <Container>
                {/* Header */}
                <div className={`cs-header ${isVisible ? "cs-header--visible" : ""}`}>
                    <span className="cs-label">✦ Our Edge</span>
                    <h2 className="cs-title">Why Choose LeadsQResearch?</h2>
                    <p className="cs-subtitle">
                        Precision-built tools and expert services that give your business the
                        competitive intelligence it deserves.
                    </p>
                </div>

                {/* Cards grid */}
                <Row className="g-4 cs-grid">
                    {features.map((feature, index) => (
                        <FeatureCard
                            key={feature.id}
                            feature={feature}
                            index={index}
                            isVisible={isVisible}
                        />
                    ))}
                </Row>
            </Container>
        </section>
    );
}
